import { useMemo } from 'react';
import { StanFilterConfig } from '@/components/core-components/StanFilters/types';
import { useDefaultStore } from '@/stores/defaultStore';
import { useStoreStore } from '@/stores';
import { buildProducts, ProductFilterOption } from '@/lib/helpers';
import { Product } from '@/types/store';
import { useStanQuery } from '@/queries';
import { QUERY_KEYS } from '@/lib/constants';
import { fetchAvailableFanTags } from '@/lib/api/audience';

/**
 * Custom hook to build customer filters with dynamic product and tag options
 */
export function useCustomerFilters(): StanFilterConfig[] {
    const store = useDefaultStore(state => state.store);
    const getFunnels = useStoreStore(state => state.getFunnels);
    const funnels = useMemo(() => getFunnels(), [getFunnels]);

    // Fetch available tags
    const { data: availableTags } = useStanQuery(
        {
            queryKey: [QUERY_KEYS.FAN_TAGS, 'available'],
            queryFn: fetchAvailableFanTags,
            staleTime: 5 * 60 * 1000, // 5 minutes
            meta: {
                error: {
                    sentryMessage: '[CUSTOMERS] failed-to-fetch-available-tags',
                },
            },
        },
        true
    );

    return useMemo(() => {
        // Build product options (may be empty array)
        const products = store
            ? (buildProducts({
                  store,
                  funnels: funnels || [],
                  format: 'options',
              }) as ProductFilterOption[])
            : [];

        // Build membership product options (may be empty array)
        const membershipProducts = store
            ? (buildProducts({
                  store,
                  funnels: funnels || [],
                  filters: {
                      excludeLinks: true,
                      membershipOnly: true,
                  },
                  format: 'products',
              }) as (Product & { membershipId?: number })[])
            : [];

        // Always include all filters, matching Vue implementation
        const filters: StanFilterConfig[] = [
            {
                id: 'audience-name',
                label: 'Name',
                type: 'text',
            },
            {
                id: 'audience-eid',
                label: 'Email',
                type: 'text',
            },
            {
                id: 'audience-since',
                label: 'Since',
                type: 'date',
            },
            {
                id: 'audience-total-purchases',
                label: 'Purchases',
                type: 'number',
            },
            {
                id: 'audience-total-spent',
                label: 'Spent',
                type: 'number',
            },
            {
                id: 'audience-product-id',
                label: 'Product',
                type: 'select',
                customDropdownReduce: 'id',
                customDropdownOptions: products.map(p => ({
                    label: p.label,
                    id: p.id,
                    icon: p.icon,
                })),
            },
            {
                id: 'audience-active-subscription',
                label: 'Active Subscription',
                type: 'select',
                customDropdownReduce: 'membershipId',
                customDropdownOptions: membershipProducts.map(p => ({
                    label: p.title,
                    membershipId: p.membershipId,
                    icon: p.image,
                })),
            },
            {
                id: 'audience-tags',
                label: 'Tag',
                type: 'select',
                customDropdownReduce: 'value',
                customDropdownOptions: (availableTags || []).map(tag => ({
                    label: tag,
                    value: tag,
                })),
            },
        ];

        return filters;
    }, [store, funnels, availableTags]);
}
